import { baseApi } from './baseApi';
import type { ApiResponse, Order } from '../../types/api';

interface OrdersQueryParams {
  page?: number;
  limit?: number;
  userId?: number;
  status?: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  section?: 'social_ar' | 'social_en' | 'games' | 'manual';
  providerId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface OrdersResponse {
  orders: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface UpdateOrderStatusRequest {
  status: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  notes?: string;
}

interface UpdateOrderStatusResponse {
  order: Order;
  message: string;
}

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<ApiResponse<OrdersResponse>, OrdersQueryParams>({
      query: (params) => ({
        url: '/orders',
        method: 'GET',
        params,
      }),
      providesTags: ['Order'],
    }),
    getOrderById: builder.query<ApiResponse<{ order: Order }>, string>({
      query: (id) => ({
        url: `/orders/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    updateOrderStatus: builder.mutation<
      ApiResponse<UpdateOrderStatusResponse>,
      { id: string; data: UpdateOrderStatusRequest }
    >({
      query: ({ id, data }) => ({
        url: `/orders/${id}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Order', id }, 'Order', 'Dashboard'],
    }),
    deleteOrder: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: (id) => ({
        url: `/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Order', id }, 'Order', 'Dashboard'],
    }),
    syncOrder: builder.mutation<ApiResponse<{ order: Order; providerStatus: unknown; message: string }>, string>({
      query: (id) => ({
        url: `/orders/${id}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Order', id }, 'Order', 'Dashboard'],
    }),
    cancelOrder: builder.mutation<ApiResponse<{ order: Order; message: string }>, string>({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Order', id }, 'Order', 'Dashboard'],
    }),
    refillOrder: builder.mutation<ApiResponse<{ order: Order; refill: unknown; message: string }>, string>({
      query: (id) => ({
        url: `/orders/${id}/refill`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Order', id }, 'Order', 'Dashboard'],
    }),
    getOrderStatus: builder.query<ApiResponse<{ orderId: string; providerOrderRef: string; providerStatus: unknown }>, string>({
      query: (id) => ({
        url: `/orders/${id}/status`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Order', id: `${id}-status` }],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useSyncOrderMutation,
  useCancelOrderMutation,
  useRefillOrderMutation,
  useGetOrderStatusQuery,
} = ordersApi;
